import { useState, useEffect, useCallback } from 'react';

const TIMEZONE_CURRENCY = {
  'America/New_York': 'USD', 'America/Chicago': 'USD', 'America/Denver': 'USD',
  'America/Los_Angeles': 'USD', 'America/Phoenix': 'USD', 'America/Anchorage': 'USD',
  'Pacific/Honolulu': 'USD',
  'America/Toronto': 'CAD', 'America/Vancouver': 'CAD', 'America/Winnipeg': 'CAD',
  'America/Halifax': 'CAD', 'America/St_Johns': 'CAD',
  'Australia/Sydney': 'AUD', 'Australia/Melbourne': 'AUD', 'Australia/Brisbane': 'AUD',
  'Australia/Perth': 'AUD', 'Australia/Adelaide': 'AUD', 'Australia/Darwin': 'AUD',
  'Pacific/Auckland': 'NZD', 'Pacific/Chatham': 'NZD',
  'Asia/Singapore': 'SGD',
  'Asia/Hong_Kong': 'HKD',
  'Asia/Tokyo': 'JPY', 'Asia/Irkutsk': 'JPY',
  'Asia/Shanghai': 'CNY', 'Asia/Chongqing': 'CNY', 'Asia/Harbin': 'CNY',
  'Asia/Urumqi': 'CNY', 'Asia/Kashgar': 'CNY',
  'Asia/Seoul': 'KRW',
  'Asia/Kolkata': 'INR', 'Asia/Calcutta': 'INR',
  'Asia/Jakarta': 'IDR', 'Asia/Makassar': 'IDR', 'Asia/Jayapura': 'IDR',
  'Asia/Kuala_Lumpur': 'MYR', 'Asia/Kuching': 'MYR',
  'Asia/Bangkok': 'THB', 'Asia/Vientiane': 'THB',
  'Asia/Manila': 'PHP',
  'Asia/Ho_Chi_Minh': 'VND', 'Asia/Hanoi': 'VND',
  'Asia/Dhaka': 'BDT',
  'Asia/Karachi': 'PKR',
  'Asia/Colombo': 'LKR',
  'Asia/Taipei': 'TWD',
  'Europe/London': 'GBP', 'Europe/Guernsey': 'GBP', 'Europe/Jersey': 'GBP',
  'Europe/Isle_of_Man': 'GBP',
  'Europe/Paris': 'EUR', 'Europe/Berlin': 'EUR', 'Europe/Madrid': 'EUR',
  'Europe/Rome': 'EUR', 'Europe/Amsterdam': 'EUR', 'Europe/Brussels': 'EUR',
  'Europe/Lisbon': 'EUR', 'Europe/Vienna': 'EUR', 'Europe/Helsinki': 'EUR',
  'Europe/Athens': 'EUR', 'Europe/Dublin': 'EUR', 'Europe/Luxembourg': 'EUR',
  'Europe/Valletta': 'EUR', 'Europe/Nicosia': 'EUR', 'Europe/Bratislava': 'EUR',
  'Europe/Ljubljana': 'EUR', 'Europe/Tallinn': 'EUR', 'Europe/Riga': 'EUR',
  'Europe/Vilnius': 'EUR', 'Europe/Zagreb': 'EUR', 'Europe/Warsaw': 'EUR',
  'Europe/Zurich': 'CHF', 'Europe/Oslo': 'NOK', 'Europe/Stockholm': 'SEK',
  'Europe/Copenhagen': 'DKK', 'Europe/Prague': 'CZK', 'Europe/Budapest': 'HUF',
  'Europe/Bucharest': 'RON', 'Europe/Sofia': 'BGN', 'Europe/Belgrade': 'RSD',
  'Europe/Kiev': 'UAH', 'Europe/Istanbul': 'TRY',
  'America/Sao_Paulo': 'BRL', 'America/Fortaleza': 'BRL', 'America/Manaus': 'BRL',
  'America/Mexico_City': 'MXN', 'America/Monterrey': 'MXN', 'America/Merida': 'MXN',
  'America/Argentina/Buenos_Aires': 'ARS',
  'America/Santiago': 'CLP',
  'America/Bogota': 'COP',
  'America/Lima': 'PEN',
  'Africa/Johannesburg': 'ZAR',
  'Africa/Lagos': 'NGN',
  'Africa/Nairobi': 'KES',
  'Africa/Accra': 'GHS',
  'Africa/Cairo': 'EGP',
  'Asia/Riyadh': 'SAR', 'Asia/Bahrain': 'BHD',
  'Asia/Dubai': 'AED',
  'Asia/Qatar': 'QAR',
  'Asia/Kuwait': 'KWD',
  'Asia/Muscat': 'OMR',
  'Asia/Jerusalem': 'ILS',
  'Europe/Moscow': 'RUB', 'Europe/Kaliningrad': 'RUB',
};

function currencyFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (TIMEZONE_CURRENCY[tz]) return TIMEZONE_CURRENCY[tz];
    return null;
  } catch (_) {
    return null;
  }
}

function currencyFromLocale() {
  try {
    const locale = navigator.language || (navigator.languages && navigator.languages[0]) || '';
    const country = locale.split('-')[1];
    const map = {
      US: 'USD', CA: 'CAD', AU: 'AUD', NZ: 'NZD', SG: 'SGD', HK: 'HKD',
      JP: 'JPY', CN: 'CNY', KR: 'KRW', IN: 'INR', ID: 'IDR', MY: 'MYR',
      TH: 'THB', PH: 'PHP', VN: 'VND', CH: 'CHF', NO: 'NOK', SE: 'SEK',
      DK: 'DKK', PL: 'PLN', CZ: 'CZK', HU: 'HUF', RO: 'RON', TR: 'TRY',
      BR: 'BRL', MX: 'MXN', AR: 'ARS', ZA: 'ZAR', SA: 'SAR', AE: 'AED',
      GB: 'GBP', GG: 'GBP', JE: 'GBP', IM: 'GBP',
      DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', PT: 'EUR',
    };
    return country ? (map[country.toUpperCase()] || null) : null;
  } catch (_) {
    return null;
  }
}

async function fetchWithTimeout(url, ms) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), ms);
  try {
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(id);
    return res;
  } catch (e) {
    clearTimeout(id);
    throw e;
  }
}

export default function useCurrencyDisplay() {
  const [currencyInfo, setCurrencyInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function detect() {
      // Method 1: browser timezone (instant, no API call, no rate limit)
      let currencyCode = currencyFromTimezone();

      // Method 2: browser locale
      if (!currencyCode) currencyCode = currencyFromLocale();

      // Method 3: ipapi.co (as enhancement/override if available)
      if (!currencyCode) {
        try {
          const res = await fetchWithTimeout('https://ipapi.co/json/', 4000);
          const data = await res.json();
          if (data && data.currency) currencyCode = data.currency;
        } catch (_) {}
      }

      if (!currencyCode || currencyCode === 'GBP' || cancelled) return;

      // Fetch live exchange rate via backend proxy (avoids CORS)
      try {
        const res = await fetchWithTimeout(
          `https://api.asumahq.com/public/exchange-rate?from=GBP&to=${currencyCode}`,
          5000
        );
        const data = await res.json();
        const rate = data && data.rates && data.rates[currencyCode];
        if (rate && !cancelled) {
          setCurrencyInfo({ code: currencyCode, rate });
        }
      } catch (_) {}
    }

    detect();
    return () => { cancelled = true; };
  }, []);

  const formatLocal = useCallback((gbpAmount) => {
    if (!currencyInfo || !gbpAmount) return null;
    const localAmount = gbpAmount * currencyInfo.rate;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyInfo.code,
        maximumFractionDigits: 2,
      }).format(localAmount);
    } catch (_) {
      return `${currencyInfo.code} ${localAmount.toFixed(2)}`;
    }
  }, [currencyInfo]);

  return { formatLocal, currencyCode: currencyInfo?.code };
}
